import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';
import { Shield, Users, CheckCircle, FileText, Search, ClipboardCheck, UserCheck, Plane, Lock } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
export function CompaniesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const {
    t
  } = useLanguage();
  // Data from translations
  const services = [{
    icon: Users,
    ...t('companies.services.items.recruitment')
  }, {
    icon: Search,
    ...t('companies.services.items.screening')
  }, {
    icon: ClipboardCheck,
    ...t('companies.services.items.compliance')
  }, {
    icon: Plane,
    ...t('companies.services.items.coordination')
  }];
  const steps = [{
    number: '01',
    icon: FileText,
    ...t('companies.steps.items')[0]
  }, {
    number: '02',
    icon: Search,
    ...t('companies.steps.items')[1]
  }, {
    number: '03',
    icon: UserCheck,
    ...t('companies.steps.items')[2]
  }, {
    number: '04',
    icon: Plane,
    ...t('companies.steps.items')[3]
  }];
  const compliance = [{
    icon: Lock,
    ...t('companies.compliance.items.nda')
  }, {
    icon: FileText,
    ...t('companies.compliance.items.verification')
  }, {
    icon: CheckCircle,
    ...t('companies.compliance.items.stcw')
  }];
  const faqs = t('companies.faq.items');
  const vesselTypes = t('companies.form.vesselTypes');
  const regions = t('companies.form.regions');
  const durations = t('companies.form.durations');
  const rankOptions = [{
    category: t('specialists.form.ranks.deck'),
    options: [t('specialists.form.ranks.options.master'), t('specialists.form.ranks.options.chiefMate'), t('specialists.form.ranks.options.secondMate'), t('specialists.form.ranks.options.thirdMate'), t('specialists.form.ranks.options.bosun'), t('specialists.form.ranks.options.ab'), t('specialists.form.ranks.options.os')]
  }, {
    category: t('specialists.form.ranks.engine'),
    options: [t('specialists.form.ranks.options.chiefEng'), t('specialists.form.ranks.options.secondEng'), t('specialists.form.ranks.options.thirdEng'), t('specialists.form.ranks.options.fourthEng'), t('specialists.form.ranks.options.eto'), t('specialists.form.ranks.options.asstEto'), t('specialists.form.ranks.options.motorman'), t('specialists.form.ranks.options.wiper')]
  }, {
    category: t('specialists.form.ranks.other'),
    options: [t('specialists.form.ranks.options.dpo'), t('specialists.form.ranks.options.sdpo'), t('specialists.form.ranks.options.craneOp'), t('specialists.form.ranks.options.rigger'), t('specialists.form.ranks.options.electrician'), t('specialists.form.ranks.options.mechanic'), t('specialists.form.ranks.options.other')]
  }];
  return <div className="min-h-screen bg-white" data-id="element-288">
      <SEOHead page="companies" data-id="element-289" />
      <Header data-id="element-290" />

      {/* Hero Section */}
      <section className="w-full px-6 md:px-12 py-8" data-id="element-291">
        <div className="max-w-7xl mx-auto" data-id="element-292">
          {/* Desktop */}
          <div className="hidden md:block relative w-full rounded-2xl overflow-hidden" data-id="element-293">
            <img src="https://cdn.magicpatterns.com/uploads/bsYH7gzRmk2Ltz4BWbC6m7/IMG_0080.jpg" alt="Aerial view of industrial port with cargo ships" className="w-full h-[600px] lg:h-[700px] object-cover object-center" data-id="element-294" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-295" />
            <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-10" data-id="element-296">
              <div className="max-w-xl" data-id="element-297">
                <h1 className="text-2xl lg:text-4xl font-bold text-white mb-3 leading-tight drop-shadow-lg" data-id="element-298">
                  {t('companies.hero.title')}
                </h1>
                <p className="text-white/95 text-sm lg:text-base leading-relaxed mb-2 drop-shadow-md" data-id="element-299">
                  {t('companies.hero.desc1')}
                </p>
                <p className="text-white/95 text-sm lg:text-base leading-relaxed mb-4 drop-shadow-md" data-id="element-300">
                  {t('companies.hero.desc2')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3" data-id="element-301">
                  <a href="#request-form" className="px-6 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity shadow-lg inline-flex items-center justify-center" data-id="element-302">
                    {t('companies.hero.requestCrew')}
                  </a>
                  <a href="#contact" className="px-6 py-3 bg-white/95 text-[var(--color-dark)] text-sm font-medium rounded-md hover:bg-white transition-colors shadow-lg inline-flex items-center justify-center" data-id="element-303">
                    {t('companies.hero.discussTask')}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile */}
          <div className="md:hidden space-y-6" data-id="element-304">
            <h1 className="text-3xl font-bold text-[var(--color-dark)] leading-tight text-center" data-id="element-305">
              {t('companies.hero.title')}
            </h1>
            <div className="relative w-full rounded-2xl overflow-hidden" data-id="element-306">
              <img src="https://cdn.magicpatterns.com/uploads/bsYH7gzRmk2Ltz4BWbC6m7/IMG_0080.jpg" alt="Aerial view of industrial port" className="w-full h-[300px] object-cover object-center" data-id="element-307" />
            </div>
            <p className="text-[var(--color-dark)]/80 text-sm leading-relaxed text-center" data-id="element-308">
              {t('companies.hero.desc1')}
            </p>
            <div className="flex flex-col gap-3" data-id="element-309">
              <a href="#request-form" className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity inline-flex items-center justify-center" data-id="element-310">
                {t('companies.hero.requestCrew')}
              </a>
              <a href="#contact" className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity inline-flex items-center justify-center" data-id="element-311">
                {t('companies.hero.discussTask')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-312">
        <div className="max-w-7xl mx-auto" data-id="element-313">
          <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-8" data-id="element-314">
            <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-315">
              {t('companies.services.pill')}
            </span>
          </div>

          <div className="flex flex-col lg:flex-row gap-12" data-id="element-316">
            <div className="lg:w-2/5" data-id="element-317">
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-6 leading-tight whitespace-pre-line" data-id="element-318">
                {t('companies.services.title')}
              </h2>
              <p className="text-[var(--color-text-light)] text-sm leading-relaxed mb-4" data-id="element-319">
                {t('companies.services.desc1')}
              </p>
              <p className="text-[var(--color-text-light)] text-sm leading-relaxed" data-id="element-320">
                {t('companies.services.desc2')}
              </p>
            </div>

            <div className="lg:w-3/5" data-id="element-321">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-322">
                {services.map((service, index) => <div key={index} className="p-6 bg-white border border-gray-100 rounded-xl" data-id="element-323">
                    <service.icon className="w-6 h-6 text-[var(--color-tan)] mb-4" strokeWidth={1.5} data-id="element-324" />
                    <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-2" data-id="element-325">
                      {service.title}
                    </h3>
                    <p className="text-sm text-[var(--color-text-light)]" data-id="element-326">
                      {service.desc}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work - Same design as SpecialistsPage */}
      <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-327">
        <div className="max-w-5xl mx-auto" data-id="element-328">
          <div className="text-center mb-10" data-id="element-329">
            <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-5 bg-white" data-id="element-330">
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-331">
                {t('companies.steps.pill')}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-3" data-id="element-332">
              {t('companies.steps.title')}
            </h2>
            <p className="text-base text-gray-600 max-w-xl mx-auto" data-id="element-333">
              {t('companies.steps.desc')}
            </p>
          </div>

          {/* Desktop: Zigzag Cards with Connecting Lines */}
          <div className="hidden md:block relative" data-id="element-334">
            {/* Main vertical center line */}
            <div className="absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-gray-200" data-id="element-335" />

            <div className="space-y-5 relative" data-id="element-336">
              {steps.map((step, index) => {
              const isRight = index % 2 === 0;
              const StepIcon = step.icon;
              return <div key={index} className="relative" data-id="element-337">
                    {/* Horizontal branch line */}
                    <div className={`absolute top-1/2 -translate-y-1/2 h-px bg-gray-200 ${isRight ? 'left-1/2 w-[8%]' : 'right-1/2 w-[8%]'}`} data-id="element-338" />

                    {/* Center dot */}
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[var(--color-tan)] z-10" data-id="element-339" />

                    {/* Card */}
                    <div className={`flex ${isRight ? 'justify-end' : 'justify-start'}`} data-id="element-340">
                      <div className="w-[42%]" data-id="element-341">
                        <div className="bg-white border border-gray-200 rounded-xl p-5 relative" data-id="element-342">
                          <span className="absolute top-3 right-4 text-xs font-medium text-gray-400" data-id="element-343">
                            {step.number}
                          </span>
                          <div className="flex items-center gap-4 pr-10" data-id="element-344">
                            <div className="w-10 h-10 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-345">
                              <StepIcon className="w-5 h-5 text-[var(--color-tan)]" data-id="element-346" />
                            </div>
                            <div className="flex-1" data-id="element-347">
                              <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-1" data-id="element-348">
                                {step.title}
                              </h3>
                              <p className="text-sm text-gray-500 leading-relaxed" data-id="element-349">
                                {step.desc}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {/* Mobile: Vertical cards with connecting line */}
          <div className="md:hidden relative" data-id="element-350">
            <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" data-id="element-351" />

            <div className="space-y-4" data-id="element-352">
              {steps.map((step, index) => {
              const StepIcon = step.icon;
              return <div key={index} className="relative flex gap-4" data-id="element-353">
                    <div className="w-8 flex-shrink-0 flex justify-center relative z-10" data-id="element-354">
                      <div className="w-2 h-2 rounded-full bg-[var(--color-tan)] mt-6" data-id="element-355" />
                    </div>
                    <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4 relative" data-id="element-356">
                      <span className="absolute top-3 right-3 text-xs font-medium text-gray-400" data-id="element-357">
                        {step.number}
                      </span>
                      <div className="flex items-center gap-3 pr-8" data-id="element-358">
                        <div className="w-9 h-9 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-359">
                          <StepIcon className="w-4 h-4 text-[var(--color-tan)]" data-id="element-360" />
                        </div>
                        <div className="flex-1" data-id="element-361">
                          <h3 className="text-sm font-semibold text-[var(--color-text-dark)] mb-0.5" data-id="element-362">
                            {step.title}
                          </h3>
                          <p className="text-xs text-gray-500" data-id="element-363">{step.desc}</p>
                        </div>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </section>

      {/* Compliance & Confidentiality */}
      <section className="w-full px-6 md:px-12 py-16 bg-[var(--color-bg)]" data-id="element-364">
        <div className="max-w-4xl mx-auto text-center" data-id="element-365">
          <div className="inline-block px-4 py-1.5 border border-gray-300 rounded-full mb-6 bg-white" data-id="element-366">
            <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-367">
              {t('companies.compliance.pill')}
            </span>
          </div>
          <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-368">
            {t('companies.compliance.title')}
          </h2>
          <p className="text-lg text-[var(--color-text-light)] mb-10" data-id="element-369">
            {t('companies.compliance.desc')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-370">
            {compliance.map((item, index) => <div key={index} className="p-6 bg-white rounded-xl border border-gray-200" data-id="element-371">
                <div className="w-12 h-12 rounded-full bg-[var(--color-tan)]/10 flex items-center justify-center mx-auto mb-4" data-id="element-372">
                  <item.icon className="w-6 h-6 text-[var(--color-tan)]" data-id="element-373" />
                </div>
                <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-2" data-id="element-374">
                  {item.title}
                </h3>
                <p className="text-sm text-[var(--color-text-light)]" data-id="element-375">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-376">
        <div className="max-w-4xl mx-auto text-center" data-id="element-377">
          <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-6" data-id="element-378">
            <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-379">
              {t('companies.faq.pill')}
            </span>
          </div>
          <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-12" data-id="element-380">
            {t('companies.faq.title')}
          </h2>

          <div className="bg-white rounded-2xl p-6 md:p-8 border-2 border-gray-200 text-left" data-id="element-381">
            <div className="space-y-0" data-id="element-382">
              {faqs.map((faq: any, index: number) => <div key={index} className="border-b border-gray-100 last:border-0" data-id="element-383">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full py-5 flex items-start justify-between text-left gap-4" aria-expanded={openFaq === index} data-id="element-384">
                    <span className="text-base font-medium text-[var(--color-text-dark)]" data-id="element-385">
                      {faq.q}
                    </span>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${openFaq === index ? 'bg-[var(--color-tan)]' : 'bg-gray-100'}`} data-id="element-386">
                      <svg className={`w-4 h-4 transition-colors ${openFaq === index ? 'text-white' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-387">
                        {openFaq === index ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" data-id="element-388" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" data-id="element-389" />}
                      </svg>
                    </div>
                  </button>
                  {/* Content stays in DOM for SEO - hidden via CSS */}
                  <div className={`overflow-hidden transition-all duration-200 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-390">
                    <p className="pb-5 text-sm text-[var(--color-text-light)]" data-id="element-391">
                      {faq.a}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Request Form */}
      <section id="request-form" className="w-full px-6 md:px-12 py-16 bg-[var(--color-bg)]" data-id="element-392">
        <div className="max-w-4xl mx-auto text-center" data-id="element-393">
          <div className="inline-block px-4 py-1.5 border border-gray-300 rounded-full mb-6 bg-white" data-id="element-394">
            <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-395">
              {t('companies.form.pill')}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-396">
            {t('companies.form.title')}
          </h2>
          <p className="text-lg text-[var(--color-text-light)] mb-2" data-id="element-397">
            {t('companies.form.desc1')}
          </p>
          <p className="text-base text-[var(--color-text-light)] mb-8" data-id="element-398">
            {t('companies.form.desc2')}
          </p>

          <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 md:p-8 text-left" data-id="element-399">
            <form className="space-y-6" data-id="element-400">
              {/* Company Information */}
              <div data-id="element-401">
                <h3 className="text-lg font-semibold text-[var(--color-text-dark)] mb-4" data-id="element-402">
                  {t('companies.form.sections.company')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-403">
                  <div data-id="element-404">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-405">
                      {t('companies.form.labels.companyName')}{' '}
                      <span className="text-red-500" data-id="element-406">*</span>
                    </label>
                    <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-407" />
                  </div>
                  <div data-id="element-408">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-409">
                      {t('companies.form.labels.contactPerson')}{' '}
                      <span className="text-red-500" data-id="element-410">*</span>
                    </label>
                    <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-411" />
                  </div>
                  <div data-id="element-412">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-413">
                      {t('companies.form.labels.email')}{' '}
                      <span className="text-red-500" data-id="element-414">*</span>
                    </label>
                    <input type="email" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-415" />
                  </div>
                  <div data-id="element-416">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-417">
                      {t('companies.form.labels.phone')}{' '}
                      <span className="text-red-500" data-id="element-418">*</span>
                    </label>
                    <input type="tel" placeholder="+1 234 567 8900" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-419" />
                  </div>
                </div>
              </div>

              {/* Request Details */}
              <div data-id="element-420">
                <h3 className="text-lg font-semibold text-[var(--color-text-dark)] mb-4" data-id="element-421">
                  {t('companies.form.sections.details')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-422">
                  <div data-id="element-423">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-424">
                      {t('companies.form.labels.vesselType')}{' '}
                      <span className="text-red-500" data-id="element-425">*</span>
                    </label>
                    <select className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center'
                  }} data-id="element-426">
                      <option value="" data-id="element-427">
                        {t('companies.form.labels.selectVessel')}
                      </option>
                      {vesselTypes.map((type: string) => <option key={type} value={type} data-id="element-428">
                          {type}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-429">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-430">
                      {t('companies.form.labels.positions')}{' '}
                      <span className="text-red-500" data-id="element-431">*</span>
                    </label>
                    <select className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center'
                  }} data-id="element-432">
                      <option value="" data-id="element-433">
                        {t('companies.form.labels.selectPositions')}
                      </option>
                      {rankOptions.map(group => <optgroup key={group.category} label={group.category} data-id="element-434">
                          {group.options.map(option => <option key={option} value={option} data-id="element-435">
                              {option}
                            </option>)}
                        </optgroup>)}
                    </select>
                  </div>
                  <div data-id="element-436">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-437">
                      {t('companies.form.labels.crewNumber')}{' '}
                      <span className="text-red-500" data-id="element-438">*</span>
                    </label>
                    <input type="number" min="1" placeholder="e.g., 5" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-439" />
                  </div>
                  <div data-id="element-440">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-441">
                      {t('companies.form.labels.location')}
                    </label>
                    <select className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center'
                  }} data-id="element-442">
                      <option value="" data-id="element-443">
                        {t('companies.form.labels.selectLocation')}
                      </option>
                      {regions.map((region: string) => <option key={region} value={region} data-id="element-444">
                          {region}
                        </option>)}
                    </select>
                  </div>
                </div>

                <div className="mt-4" data-id="element-445">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-446">
                    {t('companies.form.labels.additional')}
                  </label>
                  <textarea rows={4} placeholder={t('companies.form.labels.additionalPlaceholder')} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors resize-none" data-id="element-447" />
                </div>
              </div>

              {/* Timeline */}
              <div data-id="element-448">
                <h3 className="text-lg font-semibold text-[var(--color-text-dark)] mb-4" data-id="element-449">
                  {t('companies.form.sections.timeline')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-450">
                  <div data-id="element-451">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-452">
                      {t('companies.form.labels.startDate')}{' '}
                      <span className="text-red-500" data-id="element-453">*</span>
                    </label>
                    <input type="date" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-454" />
                  </div>
                  <div data-id="element-455">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-456">
                      {t('companies.form.labels.duration')}
                    </label>
                    <select className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center'
                  }} data-id="element-457">
                      <option value="" data-id="element-458">
                        {t('companies.form.labels.selectDuration')}
                      </option>
                      <option value="1-month" data-id="element-459">{durations.m1}</option>
                      <option value="2-months" data-id="element-460">{durations.m2}</option>
                      <option value="3-months" data-id="element-461">{durations.m3}</option>
                      <option value="4-months" data-id="element-462">{durations.m4}</option>
                      <option value="6-months" data-id="element-463">{durations.m6}</option>
                      <option value="9-months" data-id="element-464">{durations.m9}</option>
                      <option value="12-months" data-id="element-465">{durations.m12}</option>
                      <option value="ongoing" data-id="element-466">{durations.ongoing}</option>
                      <option value="project-based" data-id="element-467">{durations.project}</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Consent */}
              <div className="pt-4" data-id="element-468">
                <label className="flex items-start gap-3 cursor-pointer" data-id="element-469">
                  <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-1 w-4 h-4 text-[var(--color-tan)] border-gray-300 rounded focus:ring-[var(--color-tan)]" data-id="element-470" />
                  <span className="text-sm text-[var(--color-text-dark)]" data-id="element-471">
                    {t('companies.form.labels.consent')}{' '}
                    <span className="text-red-500" data-id="element-472">*</span>
                  </span>
                </label>
              </div>

              <div className="flex justify-center pt-2" data-id="element-473">
                <button type="submit" disabled={!agreedToTerms} className={`px-8 py-4 text-white text-base font-medium rounded-lg transition-opacity flex items-center gap-2 ${agreedToTerms ? 'bg-[var(--color-tan)] hover:opacity-90' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-474">
                  <CheckCircle className="w-5 h-5" data-id="element-475" />
                  {t('companies.form.labels.submit')}
                </button>
              </div>

              <p className="text-xs text-[var(--color-text-light)] text-center" data-id="element-476">
                {t('companies.form.labels.disclaimer')}
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="w-full px-6 md:px-12 py-16 bg-[var(--color-dark)]" data-id="element-477">
        <div className="max-w-4xl mx-auto text-center" data-id="element-478">
          <div className="inline-block px-4 py-1.5 border border-gray-600 rounded-full mb-6" data-id="element-479">
            <span className="text-sm font-medium text-white" data-id="element-480">
              {t('companies.contact.pill')}
            </span>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-481">
            {t('companies.contact.title')}
          </h2>
          <p className="text-lg text-gray-400 mb-8" data-id="element-482">
            {t('companies.contact.desc')}
          </p>

          <div className="flex flex-wrap justify-center gap-4" data-id="element-483">
            <a href="https://t.me/elvora" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-484">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-485">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" data-id="element-486" />
              </svg>
              <span className="text-sm font-medium" data-id="element-487">Telegram</span>
            </a>
            <a href="https://wa.me/380" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-488">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-489">
                <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2z" data-id="element-490" />
              </svg>
              <span className="text-sm font-medium" data-id="element-491">WhatsApp</span>
            </a>
            <a href="mailto:info@elvora.com" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-492">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-493">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-494" />
              </svg>
              <span className="text-sm font-medium" data-id="element-495">Email</span>
            </a>
          </div>
        </div>
      </section>

      <Footer data-id="element-496" />
    </div>;
}